import { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// hooks
import { useUpdateAccount } from '../../../../hooks/useUpdateAccount';
import { useSetUsername } from '../../../../hooks/useSetUsername';

// actions
import { setSystemNotification } from '../../../../store/system/system.action';

// styles
import {
  AccountDetailsContainer,
  InputContainer
} from './AccountDetails.styles.js';

// components
import ActionField from '../../../../components/action-field/ActionField';
import InputBar from '../../../../components/input-bar/InputBar';
import PrimaryBtn from '../../../../components/btns/primary-btn/PrimaryBtn';

// data
import TeamSelection from './components/team-selection/TeamSelection.js';
import { selectUser, selectEmailVerificationPending } from '../../../../store/user/user.selector';

// MUI Icons
import { Error, CheckCircle } from '@mui/icons-material';

export default function AccountDetails() {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const emailVerificationPending = useSelector(selectEmailVerificationPending);
  const { updateUserEmail, refreshUserEmailVerification, error, isPending, clearError } = useUpdateAccount();
  const { setUsername: setUsernameFunction, isSettingUsername, error: usernameError } = useSetUsername();

  const [savedUsername, setSavedUsername] = useState('');
  const [username, setUsername] = useState('');
  const [savedEmail, setSavedEmail] = useState('');
  const [email, setEmail] = useState('');
  const [showEmailTooltip, setShowEmailTooltip] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const emailSectionRef = useRef(null);
  const usernameSectionRef = useRef(null);

  useEffect(() => {
    if (user) {
      setSavedUsername(user.username || '')
      setUsername(user.username || '')
      setSavedEmail(user.email || '')
      setEmail(user.email || '')
    } else {
      setSavedUsername("")
      setUsername("")
      setSavedEmail("")
      setEmail("")
    }
  }, [user])

  // HANDLE USERNAME INPUT CHANGE (auto-convert to lowercase, remove spaces)
  const handleUsernameChange = (value) => {
    const cleanValue = value.replace(/\s/g, '').toLowerCase();
    setUsername(cleanValue);
  };

  // HANDLE USERNAME BLUR (revert only when leaving the section, not when clicking Update button)
  const handleUsernameBlur = (e) => {
    if (username === savedUsername) return;
    const nextFocus = e.relatedTarget;
    if (nextFocus && usernameSectionRef.current?.contains(nextFocus)) return;
    requestAnimationFrame(() => {
      if (document.activeElement && usernameSectionRef.current?.contains(document.activeElement)) return;
      setUsername(savedUsername);
    });
  };

  // HANDLE EMAIL INPUT CHANGE (remove spaces)
  const handleEmailChange = (value) => {
    const cleanValue = value.replace(/\s/g, '');
    setEmail(cleanValue);
  };

  // HANDLE EMAIL BLUR (revert only when leaving the section, not when moving to Current Password)
  const handleEmailBlur = (e) => {
    if (email === savedEmail) return;
    const nextFocus = e.relatedTarget;
    if (nextFocus && emailSectionRef.current?.contains(nextFocus)) return;
    requestAnimationFrame(() => {
      if (document.activeElement && emailSectionRef.current?.contains(document.activeElement)) return;
      setEmail(savedEmail);
      setCurrentPassword('');
    });
  };

  // HANDLE UPDATING USERNAME
  const handleUpdateUsername = async () => {
    if (username === savedUsername) {
      return;
    }

    const result = await setUsernameFunction(username);

    if (result && result.success) {
      setSavedUsername(username);
      dispatch(setSystemNotification("Username updated successfully!"));
    } else if (result && result.error) {
      setUsername(savedUsername);
      dispatch(setSystemNotification(`Failed to update username: ${result.error}`));
    }
  };

  // HANDLE UPDATING EMAIL
  const handleUpdateEmail = async () => {
    if (email === savedEmail) {
      return;
    }

    await refreshUserEmailVerification();

    const result = await updateUserEmail(email, savedEmail, currentPassword);

    if (result && result.success) {
      setCurrentPassword(''); // Clear password field
      dispatch(setSystemNotification(result.message || "Verification email sent! Please check your new email."));
    } else if (result && result.error) {
      if (result.error === 'Please verify your current email before changing it') {
        dispatch(setSystemNotification("Please verify your email first. Check your inbox or click 'Send Verification' below."));
      } else {
        dispatch(setSystemNotification(`Failed to update email: ${result.error}`));
      }
    }
  };

  // Clear error when component unmounts or user changes
  useEffect(() => {
    if (error) {
      clearError();
    }
  }, [error, clearError]);

  // Display username error if it exists
  useEffect(() => {
    if (usernameError) {
      dispatch(setSystemNotification(`Username error: ${usernameError}`));
    }
  }, [usernameError, dispatch]);

  const showUpdateUsername = username !== savedUsername && !!username.trim();
  const isUsernameValid = /^[a-z0-9_]{3,20}$/.test(username);

  return (
    <AccountDetailsContainer>
      <TeamSelection />
      <div ref={usernameSectionRef} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <ActionField>
          <InputContainer>
            <InputBar
              label="Username"
              type="text"
              value={username}
              updateValue={handleUsernameChange}
              onBlur={handleUsernameBlur}
              onKeyDown={(e) => e.key === 'Enter' && showUpdateUsername && isUsernameValid && handleUpdateUsername()}
              placeholder="3-20 characters, lowercase letters, numbers, underscores"
            />
          </InputContainer>
          {showUpdateUsername && (
            <PrimaryBtn
              type="default"
              title="UPDATE USERNAME"
              onClick={handleUpdateUsername}
              isDisabled={!isUsernameValid}
              isLoading={isSettingUsername}
            />
          )}
        </ActionField>
      </div>
      <div ref={emailSectionRef} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <ActionField>
        <InputContainer>
          <div style={{ position: 'relative' }}>
            <InputBar label={"Email"} type={'text'} value={email} updateValue={handleEmailChange} onBlur={handleEmailBlur} />
            {user && user.email && email === savedEmail && (
              <div
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer'
                }}
                onMouseEnter={() => setShowEmailTooltip(true)}
                onMouseLeave={() => setShowEmailTooltip(false)}
              >
                {!emailVerificationPending ? (
                  <CheckCircle style={{ fontSize: '16px', color: '#4CAF50' }} />
                ) : (
                  <Error style={{ fontSize: '16px', color: '#F44336' }} />
                )}
                {showEmailTooltip && (
                  <h3 style={{
                    position: 'absolute',
                    top: '-35px',
                    right: '0',
                    backgroundColor: '#333',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                    zIndex: 1000,
                    pointerEvents: 'none',
                    margin: 0
                  }}>
                    {!emailVerificationPending ? 'Email is verified.' : 'Email not verified.'}
                  </h3>
                )}
              </div>
            )}
          </div>
        </InputContainer>

      </ActionField>
      {email !== savedEmail && (
        <ActionField>
          <InputContainer>
            <InputBar label={"Current Password"} type={'password'} value={currentPassword} updateValue={setCurrentPassword} placeholder="Enter your current password for email update" showPasswordToggle />
          </InputContainer>
          <PrimaryBtn title='UPDATE EMAIL' onClick={handleUpdateEmail} isDisabled={currentPassword.length === 0} isLoading={isPending} />
        </ActionField>
      )}
      </div>
      <ActionField>
        <InputContainer>
          <InputBar
            label="Role"
            type="text"
            value={user?.role === 'admin' ? 'Admin' : user?.role === 'user' ? 'Collector' : user?.role ?? '(none)'}
            updateValue={() => { }}
            readOnly
          />
        </InputContainer>
      </ActionField>
    </AccountDetailsContainer>
  )
}